import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { DataTable } from '@unified-checkout/design-system';
import useNavigateWithParams from 'libs/design-system/src/hooks/useNavigateWithParams';
import {
  ArrowLeft,
  ArrowRight,
} from '@unified-checkout/design-system/icons/index';

import { useLangStore } from '@app/dashboard/store/lang';

import type { IActiveBalance } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { getTransfers } from '@app/dashboard/services/inner_layout/balance_and_transfers/transfers';

import { useTransfersTableLogic } from '../../../hooks/useTransfersTableLogic';
import { handleStoreLastAction } from '../../../utils/events-tracking';
import TransferDrawerDetails from '../../../components/TransferDrawerDetails';

import styles from './recent-transfers.module.scss';

const RecentTransfers = ({
  activeBalance,
}: {
  activeBalance: IActiveBalance | null;
}) => {
  const { t } = useTranslation();

  const navigateWithParams = useNavigateWithParams();
  const {
    accountId,
    haveAccounts,
    shouldDisableQuery,
    transfersQueryKey,
    columns,
    drawerState,
    handleCloseDrawer,
    handleTableRowClick,
    handleResponseChange,
    transfersCount,
  } = useTransfersTableLogic({
    activeBalance,
  });

  const { lang } = useLangStore();

  const queryFunctionParams = useMemo(() => {
    if (haveAccounts === false) return;

    return {
      bank_account_number: accountId,
    };
  }, [lang, accountId, haveAccounts]);

  const handleViewAllTransfers = () => {
    const eventName =
      BALANCE_TRANSFERS_EVENTS.BALANCE_PAGE.VIEW_ALL_TRANSFERS_CLICKED;
    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ACCOUNT_ID]:
        activeBalance?.account?.id,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.CURRENCY]:
        activeBalance?.currency,
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFERS_TABLE
        .RECENT_TRANSFER_COUNT]: transfersCount || 0,
    });

    handleStoreLastAction(eventName);

    navigateWithParams(INNER_ROUTES.TRANSFERS);
  };

  return (
    <div className={styles['recent-transfers']}>
      <TransferDrawerDetails
        drawerState={drawerState}
        handleCloseDrawer={handleCloseDrawer}
      />
      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          dataLocation: 'data.data.results',
          columnsMap: columns,
          rowClickHandler: handleTableRowClick,
        }}
        header={{
          title: t('innerLayout.balanceTransfers.balance.recentTransfers'),
          actionBtns: [
            {
              text: t('innerLayout.balanceTransfers.balance.viewAllTransfers'),
              onClickHandler: handleViewAllTransfers,
              variant: 'outlined',
              color: 'secondary',
              trailingIcon: lang === 'ar' ? ArrowLeft : ArrowRight,
            },
          ],
        }}
        queries={{
          enabledQuery: !shouldDisableQuery,
          queryKeys: transfersQueryKey,
          queryFunction: getTransfers,
          queryFunctionParams,
          onResponseChange: handleResponseChange,
        }}
        emptyState={{
          text: t('innerLayout.balanceTransfers.table.emptyState.noTransfers'),
          description: t('innerLayout.balanceTransfers.table.emptyState.hint'),
        }}
      />
    </div>
  );
};

export default RecentTransfers;
